import React, { useState, useEffect } from "react";
import { postHistoryService } from "./services/postHistoryService";
import "./AdminDashboard.css";

const AdminPostHistoryManagement = () => {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    failed: 0,
    totalPointsUsed: 0,
    totalFreeUsed: 0,
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [historiesPerPage] = useState(20);

  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterUserId, setFilterUserId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Modal
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Lấy lịch sử và thống kê
      const [historiesResult, statsResult] = await Promise.all([
        postHistoryService.getAllHistory(500),
        postHistoryService.getHistoryStats(),
      ]);

      if (historiesResult.success) {
        setHistories(historiesResult.data);
      } else {
        setError(historiesResult.error);
      }

      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter histories
  const filteredHistories = histories.filter((history) => {
    let matches = true;

    // Status filter
    if (filterStatus !== "all" && history.status !== filterStatus) {
      matches = false;
    }

    // User ID filter
    if (filterUserId && !history.userId.includes(filterUserId)) {
      matches = false;
    }

    // Search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const titleMatch = history.postData?.title
        ?.toLowerCase()
        .includes(searchLower);
      const requirementsMatch = history.postData?.requirements
        ?.toLowerCase()
        .includes(searchLower);
      const siteNameMatch = history.wordpressData?.siteName
        ?.toLowerCase()
        .includes(searchLower);

      if (!titleMatch && !requirementsMatch && !siteNameMatch) {
        matches = false;
      }
    }

    // Date filters
    if (dateFrom) {
      const historyDate = new Date(
        history.createdAt?.toDate
          ? history.createdAt.toDate()
          : history.createdAt
      );
      const fromDate = new Date(dateFrom);
      if (historyDate < fromDate) {
        matches = false;
      }
    }

    if (dateTo) {
      const historyDate = new Date(
        history.createdAt?.toDate
          ? history.createdAt.toDate()
          : history.createdAt
      );
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (historyDate > toDate) {
        matches = false;
      }
    }

    return matches;
  });

  // Pagination
  const indexOfLastHistory = currentPage * historiesPerPage;
  const indexOfFirstHistory = indexOfLastHistory - historiesPerPage;
  const currentHistories = filteredHistories.slice(
    indexOfFirstHistory,
    indexOfLastHistory
  );
  const totalPages = Math.ceil(filteredHistories.length / historiesPerPage);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: "badge-success",
      failed: "badge-danger",
      processing: "badge-warning",
    };

    const labels = {
      success: "Thành công",
      failed: "Thất bại",
      processing: "Đang xử lý",
    };

    return (
      <span className={`badge ${badges[status] || "badge-secondary"}`}>
        {labels[status] || status}
      </span>
    );
  };

  const openModal = (history) => {
    setSelectedHistory(history);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedHistory(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Đang tải dữ liệu lịch sử bài viết...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h3>Lỗi tải dữ liệu</h3>
        <p>{error}</p>
        <button onClick={fetchData} className="btn btn-primary">
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="admin-content">
      <div className="admin-header">
        <h2>Quản Lý Lịch Sử Tạo Bài Viết Website</h2>
        <button onClick={fetchData} className="btn btn-primary">
          Làm mới
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Tổng Số Bài Viết</h3>
          <p className="stat-number">{stats.total}</p>
        </div>
        <div className="stat-card success">
          <h3>Thành Công</h3>
          <p className="stat-number">{stats.success}</p>
        </div>
        <div className="stat-card danger">
          <h3>Thất Bại</h3>
          <p className="stat-number">{stats.failed}</p>
        </div>
        <div className="stat-card warning">
          <h3>Tổng Điểm Đã Dùng</h3>
          <p className="stat-number">{stats.totalPointsUsed}</p>
        </div>
        <div className="stat-card info">
          <h3>Lượt Miễn Phí Đã Dùng</h3>
          <p className="stat-number">{stats.totalFreeUsed}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label>Trạng thái:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-control"
            >
              <option value="all">Tất cả</option>
              <option value="success">Thành công</option>
              <option value="failed">Thất bại</option>
              <option value="processing">Đang xử lý</option>
            </select>
          </div>

          <div className="filter-group">
            <label>User ID:</label>
            <input
              type="text"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              placeholder="Nhập User ID..."
              className="form-control"
            />
          </div>

          <div className="filter-group">
            <label>Tìm kiếm:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm theo tiêu đề, yêu cầu, tên site..."
              className="form-control"
            />
          </div>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>Từ ngày:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="form-control"
            />
          </div>

          <div className="filter-group">
            <label>Đến ngày:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="form-control"
            />
          </div>

          <div className="filter-group">
            <button
              onClick={() => {
                setFilterStatus("all");
                setFilterUserId("");
                setSearchTerm("");
                setDateFrom("");
                setDateTo("");
                setCurrentPage(1);
              }}
              className="btn btn-secondary"
            >
              Xóa bộ lọc
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <p>
          Hiển thị {indexOfFirstHistory + 1} -{" "}
          {Math.min(indexOfLastHistory, filteredHistories.length)} trong tổng số{" "}
          {filteredHistories.length} bài viết
        </p>
      </div>

      {/* History Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Thời gian</th>
              <th>User ID</th>
              <th>Tiêu đề</th>
              <th>Website</th>
              <th>Ngôn ngữ</th>
              <th>Trạng thái</th>
              <th>Điểm</th>
              <th>Đã đăng</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {currentHistories.map((history) => (
              <tr key={history.id}>
                <td>{formatDate(history.createdAt)}</td>
                <td>
                  <span className="user-id">
                    {history.userId.substring(0, 8)}...
                  </span>
                </td>
                <td>
                  <div className="post-title">
                    {history.postData?.title || "Chưa có tiêu đề"}
                  </div>
                  <div className="post-requirements">
                    {history.postData?.requirements?.substring(0, 50)}
                    {history.postData?.requirements?.length > 50 ? "..." : ""}
                  </div>
                </td>
                <td>{history.wordpressData?.siteName || "N/A"}</td>
                <td>{history.postData?.language || "vi"}</td>
                <td>{getStatusBadge(history.status)}</td>
                <td>
                  {(() => {
                    const pointsData = history.pointsData;
                    const usedFree = pointsData?.usedFreeQuantity;
                    const deductedPoints =
                      pointsData?.pointsDeductedAmount || 0;
                    const hasPointsDeducted = pointsData?.pointsDeducted;
                    const paymentType = pointsData?.paymentType;

                    // Ưu tiên hiển thị theo paymentType từ backend nếu có
                    if (paymentType) {
                      switch (paymentType) {
                        case "free_post":
                          return (
                            <span className="badge badge-info">Miễn phí</span>
                          );
                        case "points_deducted":
                          return (
                            <span className="points-used">
                              {deductedPoints} điểm
                            </span>
                          );
                        case "premium_unlimited":
                          return (
                            <span className="badge badge-success">Gói VIP</span>
                          );
                        default:
                          return <span className="text-muted">N/A</span>;
                      }
                    }

                    // Fallback logic cho dữ liệu cũ
                    // Ưu tiên trừ điểm nếu có
                    if (hasPointsDeducted && deductedPoints > 0) {
                      return (
                        <div className="points-display">
                          <span className="points-used">
                            {deductedPoints} điểm
                          </span>
                          {usedFree && (
                            <span
                              className="badge badge-warning"
                              style={{ marginLeft: "5px", fontSize: "10px" }}
                            >
                              Có lỗi logic
                            </span>
                          )}
                        </div>
                      );
                    }

                    // Chỉ dùng miễn phí
                    if (usedFree) {
                      return <span className="badge badge-info">Miễn phí</span>;
                    }

                    // Không có thông tin điểm
                    return <span className="text-muted">N/A</span>;
                  })()}
                </td>
                <td>
                  {history.wordpressData?.published ? (
                    <span className="badge badge-success">✓ Đã đăng</span>
                  ) : (
                    <span className="badge badge-secondary">Chưa đăng</span>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => openModal(history)}
                    className="btn btn-sm btn-primary"
                  >
                    Chi tiết
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn btn-secondary"
          >
            Trước
          </button>

          <span className="pagination-info">
            Trang {currentPage} / {totalPages}
          </span>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="btn btn-secondary"
          >
            Sau
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showModal && selectedHistory && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Chi Tiết Lịch Sử Bài Viết</h3>
              <button onClick={closeModal} className="modal-close">
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Basic Info */}
              <div className="detail-section">
                <h4>Thông Tin Cơ Bản</h4>
                <div className="detail-grid">
                  <div>
                    <strong>ID:</strong> {selectedHistory.id}
                  </div>
                  <div>
                    <strong>User ID:</strong> {selectedHistory.userId}
                  </div>
                  <div>
                    <strong>Trạng thái:</strong>{" "}
                    {getStatusBadge(selectedHistory.status)}
                  </div>
                  <div>
                    <strong>Thời gian tạo:</strong>{" "}
                    {formatDate(selectedHistory.createdAt)}
                  </div>
                  <div>
                    <strong>Cập nhật lần cuối:</strong>{" "}
                    {formatDate(selectedHistory.updatedAt)}
                  </div>
                  {selectedHistory.jobId && (
                    <div>
                      <strong>Job ID:</strong> {selectedHistory.jobId}
                    </div>
                  )}
                </div>
              </div>

              {/* Post Data */}
              <div className="detail-section">
                <h4>Thông Tin Bài Viết</h4>
                <div className="detail-grid">
                  <div>
                    <strong>Tiêu đề:</strong>{" "}
                    {selectedHistory.postData?.title || "Chưa có"}
                  </div>
                  <div>
                    <strong>Ngôn ngữ:</strong>{" "}
                    {selectedHistory.postData?.language || "vi"}
                  </div>
                  <div>
                    <strong>Độ dài:</strong>{" "}
                    {selectedHistory.postData?.postLength}
                  </div>
                  <div>
                    <strong>Phong cách:</strong>{" "}
                    {selectedHistory.postData?.postStyles?.join(", ")}
                  </div>
                  <div>
                    <strong>Độ dài nội dung:</strong>{" "}
                    {selectedHistory.postData?.contentLength || 0} ký tự
                  </div>
                  <div>
                    <strong>Số ảnh:</strong>{" "}
                    {selectedHistory.stats?.imageCount || 0}
                  </div>
                </div>

                {selectedHistory.postData?.requirements && (
                  <div className="detail-full-width">
                    <strong>Yêu cầu:</strong>
                    <div className="requirements-text">
                      {selectedHistory.postData.requirements}
                    </div>
                  </div>
                )}

                {selectedHistory.postData?.urls?.length > 0 && (
                  <div className="detail-full-width">
                    <strong>URLs tham khảo:</strong>
                    <ul>
                      {selectedHistory.postData.urls.map((url, index) => (
                        <li key={index}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* WordPress Data */}
              <div className="detail-section">
                <h4>Thông Tin WordPress</h4>
                <div className="detail-grid">
                  <div>
                    <strong>Tên site:</strong>{" "}
                    {selectedHistory.wordpressData?.siteName}
                  </div>
                  <div>
                    <strong>URL:</strong>{" "}
                    {selectedHistory.wordpressData?.siteUrl}
                  </div>
                  <div>
                    <strong>Trạng thái bài viết:</strong>{" "}
                    {selectedHistory.wordpressData?.status}
                  </div>
                  <div>
                    <strong>Đã đăng:</strong>{" "}
                    {selectedHistory.wordpressData?.published ? "Có" : "Không"}
                  </div>
                  {selectedHistory.wordpressData?.publishedAt && (
                    <div>
                      <strong>Thời gian đăng:</strong>{" "}
                      {formatDate(selectedHistory.wordpressData.publishedAt)}
                    </div>
                  )}
                  <div>
                    <strong>Tags:</strong>{" "}
                    {selectedHistory.wordpressData?.tags || "Không có"}
                  </div>
                </div>
              </div>

              {/* Points Data */}
              <div className="detail-section">
                <h4>Thông Tin Điểm</h4>
                <div className="detail-grid">
                  <div>
                    <strong>Gói membership:</strong>{" "}
                    {selectedHistory.pointsData?.userMembership}
                  </div>
                  <div>
                    <strong>Dùng lượt miễn phí:</strong>{" "}
                    {selectedHistory.pointsData?.usedFreeQuantity
                      ? "Có"
                      : "Không"}
                  </div>
                  <div>
                    <strong>Đã trừ điểm:</strong>{" "}
                    {selectedHistory.pointsData?.pointsDeducted
                      ? "Có"
                      : "Không"}
                  </div>
                  <div>
                    <strong>Số điểm trừ:</strong>{" "}
                    {selectedHistory.pointsData?.pointsDeductedAmount || 0}
                  </div>
                  <div>
                    <strong>Đã hoàn điểm:</strong>{" "}
                    {selectedHistory.pointsData?.pointsRefunded
                      ? "Có"
                      : "Không"}
                  </div>
                  <div>
                    <strong>Số điểm hoàn:</strong>{" "}
                    {selectedHistory.pointsData?.pointsRefundedAmount || 0}
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {selectedHistory.status === "failed" &&
                selectedHistory.errorMessage && (
                  <div className="detail-section">
                    <h4>Thông Tin Lỗi</h4>
                    <div className="error-message">
                      {selectedHistory.errorMessage}
                    </div>
                  </div>
                )}

              {/* Content Preview */}
              {selectedHistory.postData?.content && (
                <div className="detail-section">
                  <h4>Nội Dung Bài Viết</h4>
                  <div className="content-preview">
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          selectedHistory.postData.content.substring(0, 1000) +
                          (selectedHistory.postData.content.length > 1000
                            ? "..."
                            : ""),
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPostHistoryManagement;
